import React, { Component } from "react";
import { Paper, Grid, Box } from "@material-ui/core";
import { scroller } from "react-scroll";
import ReactPlayer from "react-player/lazy";
import ReactResizeDetector from "react-resize-detector";

import { IMiniUser, IWithballData } from "types";
import { getMatchVideo } from "apis";

import { EventFilter } from "./EventFilter";
import { EventList } from "./EventList";

interface ISize {
  width: number;
  height: number;
}

interface IReactPlayerProgress {
  loaded: number;
  loadedSeconds: number;
  played: number;
  playedSeconds: number;
}

type EventVideoViewProps = {
  disablePlayerSelecter?: boolean;
  dataArray: IWithballData[];
  players: IMiniUser[];
  matchId: string;
};

type EventVideoViewState = {
  playing: boolean;
  data: IWithballData[];
  playerFilter: number[];
  categoryFilter: number[];
  resultFilter: boolean[];
  possessionFilter: string[];
  url: string;
};

const calculateHeight = (width: number) => {
  return (width * 9) / 16;
};

const videoBackTrack = 5;
const videoForwardTrack = 2;

const filterEvents = (
  events: IWithballData[],
  playerFilter: number[],
  categoryFilter: number[],
  resultFilter: boolean[],
  possessionFilter: string[]
): IWithballData[] => {
  let filteredEvents = events;

  if (playerFilter.length > 0) {
    filteredEvents = filteredEvents.filter(
      (event) =>
        playerFilter.includes(event.fromPlayer) ||
        playerFilter.includes(event.toPlayer)
    );
  }

  if (categoryFilter.length > 0) {
    filteredEvents = filteredEvents.filter((event) =>
      categoryFilter.includes(event.action)
    );
  }

  if (resultFilter.length > 0) {
    filteredEvents = filteredEvents.filter((event) =>
      resultFilter.includes(event.succeed)
    );
  }

  if (possessionFilter.length > 0) {
    filteredEvents = filteredEvents.filter((event) =>
      possessionFilter.includes(event.possession)
    );
  }

  return filteredEvents;
};

// withBallData must be sorted by created_at in ascending order
const buildPlayTimeChunk = (
  withBallData: IWithballData[],
  eventIds: number[],
  duration: number
): number[][] => {
  const res: number[][] = [];
  const selectedEvents = withBallData.filter((event) =>
    eventIds.includes(event.id)
  );

  if (selectedEvents.length === 0) {
    return res;
  }

  selectedEvents.map((event) => {
    const start = Math.max(0, event.created_at - videoBackTrack);
    const end = Math.min(duration, event.created_at + videoForwardTrack);

    const chunk = res.find((chunk) => chunk[0] <= start && chunk[1] >= end);
    if (!chunk) {
      res.push([start, end]);
      return null;
    }

    chunk[1] = end;
    return null;
  });

  return res;
};

class EventVideoView extends Component<
  EventVideoViewProps,
  EventVideoViewState
> {
  state: EventVideoViewState = {
    playing: false,
    data: this.props.dataArray,
    playerFilter: [],
    categoryFilter: [],
    resultFilter: [],
    possessionFilter: [],
    url: "",
  };

  duration = 0;

  player: any;

  playChunks: number[][] = [];

  autoSeekFlag = false;

  handleEventPlay = (eventIds: number[]) => {
    this.playChunks = buildPlayTimeChunk(
      this.state.data,
      eventIds,
      this.duration
    );

    if (this.playChunks.length === 0) {
      return;
    }

    scroller.scrollTo("player-anchor", {});

    this.autoSeekFlag = true;

    this.player.seekTo(this.playChunks[0][0]);
    this.setState({ playing: true });
  };

  ref = (player: any) => {
    this.player = player;
  };

  handlePlayerChange = (playerIds: number[]) => {
    const events = filterEvents(
      this.props.dataArray,
      playerIds,
      this.state.categoryFilter,
      this.state.resultFilter,
      this.state.possessionFilter
    );
    this.setState({
      playerFilter: playerIds,
      data: events,
    });
  };

  handleEventCategoryChange = (eventCategories: number[]) => {
    const events = filterEvents(
      this.props.dataArray,
      this.state.playerFilter,
      eventCategories,
      this.state.resultFilter,
      this.state.possessionFilter
    );
    this.setState({
      categoryFilter: eventCategories,
      data: events,
    });
  };

  handleEventResultChange = (eventResults: boolean[]) => {
    const events = filterEvents(
      this.props.dataArray,
      this.state.playerFilter,
      this.state.categoryFilter,
      eventResults,
      this.state.possessionFilter
    );
    this.setState({
      resultFilter: eventResults,
      data: events,
    });
  };

  handleEventPossessionChange = (eventPossessions: string[]) => {
    const events = filterEvents(
      this.props.dataArray,
      this.state.playerFilter,
      this.state.categoryFilter,
      this.state.resultFilter,
      eventPossessions
    );
    this.setState({
      possessionFilter: eventPossessions,
      data: events,
    });
  };

  handleOnProgress = (progress: IReactPlayerProgress) => {
    if (this.playChunks.length === 0) {
      return;
    }

    if (
      progress.playedSeconds >= this.playChunks[this.playChunks.length - 1][1]
    ) {
      this.setState({ playing: false });
      this.playChunks = [];
      this.autoSeekFlag = false;
      return;
    }

    // find current chunk
    const currentChunk = this.playChunks.find(
      (chunk) =>
        progress.playedSeconds >= chunk[0] && progress.playedSeconds <= chunk[1]
    );

    // if in the middle of chunk, do nothing
    if (currentChunk) {
      return;
    }

    // find next chunk
    const nextChunk = this.playChunks.find(
      (chunk) => progress.playedSeconds < chunk[0]
    );

    // if no next chunk, do nothing
    if (!nextChunk) {
      return;
    }

    // seek to next chunk
    this.player.seekTo(nextChunk[0]);
  };

  handleOnDuration = (duration: number) => {
    this.duration = duration;
  };

  componentDidMount(): void {
    fetch(getMatchVideo())
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        this.setState({
          url: data[this.props.matchId],
        });
      });
  }

  render() {
    // dynamic get link from server, think json file is not bad in public folder, map match id with youtube url
    // const url = "http://localhost:3000/ystad.mp4";
    // const url = "https://www.youtube.com/watch?v=FeE6-3jRPt8&t=3844s";
    return (
      <Paper>
        <Box padding={2}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <EventFilter
                disablePlayerFilter={this.props.disablePlayerSelecter}
                players={this.props.players}
                onPlayerChange={this.handlePlayerChange}
                onEventCategoryChange={this.handleEventCategoryChange}
                onEventResultChange={this.handleEventResultChange}
                onEventPossessionChange={this.handleEventPossessionChange}
              />
            </Grid>
            <Grid item xs={9}>
              <EventList
                events={this.state.data}
                players={this.props.players}
                onEventsPlay={this.handleEventPlay}
              />
            </Grid>
          </Grid>
        </Box>
        <Box padding={2}>
          <div id="player-anchor">
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <ReactResizeDetector handleWidth>
                  {({ width }: ISize) => {
                    return (
                      <div style={{ border: "1px solid black " }}>
                        <ReactPlayer
                          ref={this.ref}
                          url={this.state.url}
                          width={width}
                          height={calculateHeight(width) || 0}
                          controls={!this.state.playing}
                          onProgress={this.handleOnProgress}
                          progressInterval={500}
                          playing={this.state.playing}
                          onDuration={this.handleOnDuration}
                        />
                      </div>
                    );
                  }}
                </ReactResizeDetector>
              </Grid>
            </Grid>
          </div>
        </Box>
      </Paper>
    );
  }
}

export default EventVideoView;
